import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Button, Container, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import ResultsCard from '../components/ResultsCard';

interface SharedResultsPageProps {
  user: { userId?: string; username?: string } | null;
  onShowLogin: () => void;
}

export default function SharedResultsPage({ user, onShowLogin }: SharedResultsPageProps) {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const score    = parseInt(params.get('score') ?? '0', 10);
  const total    = parseInt(params.get('total') ?? '10', 10);
  const modeName = params.get('mode') ?? 'Unknown';
  const team     = params.get('team') ?? null;

  // Validate params — show friendly error for bad/missing links
  if (isNaN(score) || isNaN(total) || total <= 0 || score > total) {
    return (
      <Container maxWidth="sm">
        <Box textAlign="center" mt={10}>
          <Typography variant="h6" color="text.secondary">
            This results link doesn't look right.
          </Typography>
          <Button variant="contained" sx={{ mt: 3 }} onClick={() => navigate('/quiz')}>
            Play Quizology
          </Button>
        </Box>
      </Container>
    );
  }

  const handlePlayCTA = () => {
    if (!user?.userId) onShowLogin();
    navigate('/quiz');
  };

  return (
    <Container maxWidth="sm">
      <Box py={4} display="flex" flexDirection="column" alignItems="center" gap={3}>

        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          style={{ textAlign: 'center' }}
        >
          <Typography sx={{ fontSize: '1.3rem', color: 'rgba(159,134,192,0.7)' }}>
            Someone shared their Quizzler results with you
          </Typography>
        </motion.div>

        {/* Card — view only */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          style={{ width: '100%' }}
        >
          <ResultsCard
            score={score}
            totalQuestions={total}
            modeName={modeName}
            team={team}
          />
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          style={{ textAlign: 'center' }}
        >
          <Typography sx={{ fontSize: '1.3rem', color: 'rgba(159,134,192,0.8)', mb: 1.5 }}>
            Think you can beat that score?
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={handlePlayCTA}
            sx={{
              background: '#534AB7',
              color: '#e0c9f0',
              borderRadius: '24px',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.95rem',
              px: 4, py: 1.25,
              '&:hover': { background: '#6b62c7' },
            }}
          >
            Start Quiz
          </Button>
        </motion.div>

      </Box>
    </Container>
  );
}